/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from 'react'
import styles from '../page.module.css'

import search from '../../../assets/images/search.svg'
import deleteIcon from '../../../assets/images/delete.svg'

import AddLocationMenu from '../../../components/Location/AddLocationMenu'
import DeleteLocationPopup from '../../../components/Location/DeleteLocationPopup'

import { getLocations, createLocation, deleteLocation } from '../../../api/locationsApi'
import { getOption, updateOption } from '../../../api/optionsApi'
import TopBar from '../../../components/TopBar'

const Location = () => {
    const addLocationPopupRef = useRef();
    const deleteLocationPopupRef = useRef();

    const [showAddInput, setShowAddInput] = useState(false);
    const [newLocationName, setNewLocationName] = useState("");
    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(false)
    const [isLoadingAddLocation, setIsLoadingAddLocation] = useState(false)
    const [isLoadingDeleteLocation, setIsLoadingDeleteLocation] = useState(false)

    const [locationList, setLocationList] = useState([]);
    const [searchInput, setSearchInput] = useState('');
    const [filteredLocations, setFilteredLocations] = useState([]);

    const [allowAddresses, setAllowAddresses] = useState(false);
    const [zoneRegions, setZoneRegions] = useState("");
    const [zoneRegionsError, setZoneRegionsError] = useState("");
    const [isSavingZoneRegions, setIsSavingZoneRegions] = useState(false);

    const loadLocations = async () => {
        try {
            setIsLoading(true);
            const response = await getLocations();
            if (response?.data?.length > 0) {
                setLocationList(response?.data);
                setFilteredLocations(response?.data);
            }
        } catch (error) {
            console.error('Failed to load locations', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadLocations();
    }, []);

    useEffect(() => {
        const loadZoneRegions = async () => {
            try {
                const data = await getOption('locationPincode');
                if (data?.data?.value?.record) {
                    setZoneRegions(data.data.value.record);
                }
            } catch (error) {
                console.error('Failed to load zone regions', error);
            }
        };
        loadZoneRegions();
    }, []);

    useEffect(() => {
        setFilteredLocations(locationList);
    }, [locationList]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (addLocationPopupRef.current && !addLocationPopupRef.current.contains(event.target)) {
                setShowAddInput(false);
            }
            if (deleteLocationPopupRef.current && !deleteLocationPopupRef.current.contains(event.target)) {
                setShowDeletePopup(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const [showDeletePopup, setShowDeletePopup] = useState(false);
    const [locationToDelete, setLocationToDelete] = useState(null);

    const handleSearch = (e) => {
        const input = e.target.value.toLowerCase();
        setSearchInput(input);
        if (input === '') {
            setFilteredLocations(locationList);
        } else {
            const result = locationList.filter(loc =>
                loc.toLowerCase().includes(input)
            );
            setFilteredLocations(result);
        }
    };

    const handleAddLocation = async () => {
        if (!newLocationName.trim()) {
            setError("Location name is required");
            return;
        }

        try {
            setIsLoadingAddLocation(true);
            const response = await createLocation({ cityName: newLocationName.trim() });
            if (response?.data) {
                await loadLocations()
                setNewLocationName("");
                setError("");
                setShowAddInput(false);
            }
        } catch (error) {
            console.error("Failed to create location", error);
            setError(error?.response?.data?.error?.message || "Failed to create location");
        } finally {
            setIsLoadingAddLocation(false);
        }
    };

    const handleZoneRegionsChange = (e) => {
        const value = e.target.value;
        // Allow only digits, dots, and commas
        if (value === '' || /^[\d.,\s]*$/.test(value)) {
            setZoneRegions(value);
            setZoneRegionsError("");
        }
    };

    const handleSaveZoneRegions = async () => {
        if (!zoneRegions.trim()) {
            setZoneRegionsError("Zone regions is required");
            return;
        }
        try {
            setIsSavingZoneRegions(true);
            await updateOption('locationPincode', { record: zoneRegions });
        } catch (error) {
            console.error("Failed to save zone regions", error);
        } finally {
            setIsSavingZoneRegions(false);
        }
    };

    const confirmDeleteLocation = () => {
        if (!locationToDelete) return;
        handleDeleteLocation(locationToDelete);
    };

    const handleDeleteLocation = async (id) => {
        try {
            setIsLoadingDeleteLocation(true);
            await deleteLocation(id);
            setLocationList(prev => prev.filter(loc => loc !== id));
            setShowDeletePopup(false);
            setLocationToDelete(null);
        } catch (error) {
            console.error("Failed to delete location", error);
        } finally {
            setIsLoadingDeleteLocation(false);
        }
    };

    const removeSelectedLocation = (locationId) => {
        setLocationToDelete(locationId);
        setShowDeletePopup(true);
    };

    return (
        <>
            <TopBar />
            <div className={styles.locationPage}>
                {/* Page Header */}
                <div className={styles.pageHeader}>
                    <h1 className={styles.pageTitle}>Locations</h1>
                    <button
                        className='add-btn'
                        onClick={() => { setShowAddInput(true); setError("") }}
                    >
                        Add Location
                    </button>
                </div>

                {/* Settings Section */}
                <div className={styles.locationSettings}>
                    <div className={styles.settingItem}>
                        <label className={styles.settingLabel}>Zone regions</label>
                        <textarea
                            className={styles.zoneRegionsTextarea}
                            value={zoneRegions}
                            onChange={handleZoneRegionsChange}
                            placeholder="Enter pincode separated by commas e.g. 3945, 390014, 390045, 8745"
                        />
                        {zoneRegionsError && (
                            <span className={styles.errorMessage}>{zoneRegionsError}</span>
                        )}
                    </div>
                    <div className={styles.saveButtons}>
                        <button
                            className={styles.saveButton}
                            onClick={handleSaveZoneRegions}
                            disabled={isSavingZoneRegions}
                        >
                            {isSavingZoneRegions ? <div className='ringLoader'></div> : 'Save'}
                        </button>
                    </div>
                </div>

                {/* Search Bar */}
                <div className={styles.searchWrapper}>
                    <div className={styles.searchInput}>
                        <input
                            type="text"
                            placeholder="Search locations..."
                            value={searchInput}
                            onChange={handleSearch}
                        />
                        <img src={search} alt="Search" />
                    </div>
                </div>

                {/* Location List */}
                <div className={`${styles.locationList} locationList ${(isLoading) ? 'loading-list' : ''}`} >
                    {filteredLocations.length > 0 ? (
                        filteredLocations.map((location) => (
                            <div key={location} className={styles.locationItem}>
                                <span className={styles.locationName}>{location}</span>
                                <button
                                    className={styles.deleteBtn}
                                    onClick={() => removeSelectedLocation(location)}
                                    title="Delete location"
                                >
                                    <img src={deleteIcon} alt="Delete" />
                                </button>
                            </div>
                        ))
                    ) : (
                        <div className={styles.emptyState}>
                            <p className={styles.nothingFound}>No locations found</p>
                        </div>
                    )}
                </div>
            </div>

            <AddLocationMenu
                showAddInput={showAddInput}
                newLocationName={newLocationName}
                setNewLocationName={setNewLocationName}
                error={error}
                handleSaveLocation={handleAddLocation}
                setShowAddInput={setShowAddInput}
                refrence={addLocationPopupRef}
                setError={setError}
                styles={styles}
                isLoadingAddLocation={isLoadingAddLocation}
            />
            <DeleteLocationPopup
                showDeletePopup={showDeletePopup}
                setShowDeletePopup={setShowDeletePopup}
                confirmDelete={confirmDeleteLocation}
                refrence={deleteLocationPopupRef}
                styles={styles}
                isLoadingDeleteLocation={isLoadingDeleteLocation}
            />
        </>
    );
}

export default Location;